import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Pagination from 'components/Pagination';
import UpdateProgress from './modals/UpdateProgress';
import RecipeModal from './modals/RecipeModal';

import { GET, httpOk, POST } from 'helpers/api';
import { formatDate, formatMoney } from 'helpers/helpers';
import { Link } from 'react-router-dom';

import { ReactComponent as ProductionIcon } from 'assets/icons/sidebar/production.svg';
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg';
import { ReactComponent as WarningCircleIcon } from 'assets/icons/warning_circle.svg';
import { ReactComponent as CircleCheckIcon } from 'assets/icons/header/circle_check.svg';

function Dashboard() {
    const { t } = useTranslation()

    const initFilterData = {
        page: 1,
        page_size: 20,
    }

    const [updateProgressModal, setUpdateProgressModal] = useState({ show: false, item: {} })
    const [recipeModal, setRecipeModal] = useState({ show: false, id: '' })

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({
        in_progress_orders: [],
        planned_orders: [],
        counters: {},
    })

    async function inProgressOrder(item) {
        const response = await POST(`/production/orders/${item.id}/start/`)
        if (httpOk(response)) {
            getData()
        }
    }

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const response = await GET('/production/dashboard/', filterData);

        if (httpOk(response)) {
            setData(response.data);
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <>
            <div>
                <div className="main-content-title">
                    {t('production_worker_dashboard')}
                </div>

                <div className="main-content-subtitle">
                    {t('production_orders_management')}
                </div>
            </div>

            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('total_orders')}
                            </div>
                            <div className="value">
                                {formatMoney(data?.counters?.total)}
                            </div>
                        </div>

                        <div className="icon secondary">
                            <ProductionIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('in_work')}
                            </div>
                            <div className="value text-purple">
                                {formatMoney(data?.counters?.in_progress)}
                            </div>
                        </div>

                        <div className="icon purple">
                            <ClockIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('planned')}
                            </div>
                            <div className="value text-primary">
                                {formatMoney(data?.counters?.planned)}
                            </div>
                        </div>

                        <div className="icon primary">
                            <WarningCircleIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('completed_today')}
                            </div>
                            <div className="value text-success">
                                {formatMoney(data?.counters?.completed_today)}
                            </div>
                        </div>

                        <div className="icon success">
                            <CircleCheckIcon />
                        </div>
                    </div>
                </div>
            </div>

            {data?.in_progress_orders?.length > 0 &&
                <div className="alert second alert-purple">
                    <div className="d-flex align-items-center alert-title mb-32 gap-8">
                        <ClockIcon />
                        {t('orders_in_work')}
                    </div>

                    <div className="d-flex flex-column gap-8">
                        {data?.in_progress_orders?.map((item, index) => (
                            <div className="card third" key={index}>
                                <div className="d-between mb-12">
                                    <div>
                                        <div className="fz16 text-first">
                                            {item.product_name}
                                        </div>
                                        <div className="text-fifth">
                                            {t('order_id')}: {item.order_number}
                                        </div>
                                    </div>

                                    <div className="alert alert-purple">
                                        {t('in_work')}
                                    </div>
                                </div>

                                <div className="d-between">
                                    <div className="text-second">
                                        {t('progress')}
                                    </div>

                                    <div className="text-first">
                                        {formatMoney(item.produced_quantity)} / {formatMoney(item.planned_quantity)}
                                    </div>
                                </div>

                                <div className="progress-bar mb-12">
                                    <div className="progress-percent" style={{ width: `${item.progress_percent}%` }}></div>
                                </div>

                                <div className="d-flex gap-8">
                                    <button className="dark" onClick={() => setUpdateProgressModal({ show: true, item })}>
                                        {t('update_progress')}
                                    </button>
                                    {/* <button className="white" onClick={() => setRecipeModal({ show: true, id: item.id })}>
                                        {t('view_recipe')}
                                    </button> */}
                                </div>
                            </div>
                        ))
                        }
                    </div>
                </div>
            }

            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('planned_orders')}
                    </div>

                    <Link to="/production/production">
                        <button className="dark">
                            {t('all_orders')}
                        </button>
                    </Link>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>{t('order_id')}</th>
                            <th style={{ width: '30%' }}>{t('product')}</th>
                            <th style={{ width: '20%' }}>{t('planned_volume')}</th>
                            <th style={{ width: '20%' }}>{t('start_date')}</th>
                            <th style={{ width: '10%' }}>{t('status')}</th>
                            <th style={{ width: '10%' }}>{t('actions')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data?.planned_orders?.map((item, index) => (
                            <tr key={index}>
                                <td className="text-second">{item.order_number}</td>
                                <td>{item.product_name}</td>
                                <td>{formatMoney(item.planned_quantity)} {item.unit_short_name}</td>
                                <td>{formatDate(item.planned_start_date)}</td>
                                <td>
                                    <div className={`alert alert-${item.status} w-fit-content`}>
                                        {t(item.status_label)}
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center gap-8">
                                        {item.status === 'planned' &&
                                            <button className="white small" onClick={() => inProgressOrder(item)}>
                                                {t('start_work')}
                                            </button>
                                        }
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />
            </div>

            <UpdateProgress modal={updateProgressModal.show} setModal={() => setUpdateProgressModal({ show: false })}
                item={updateProgressModal.item} getData={getData} />

            <RecipeModal modal={recipeModal.show} setModal={() => setRecipeModal({ show: false })} id={recipeModal.id} />
        </>
    )
}

export default Dashboard